import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { useCompany } from "../../contexts/CompanyContext";
import { useParams } from "react-router-dom";
import { supabase } from "../../lib/supabase_client";
import { useSession } from "../../contexts/SessionContext";
import { generateSSTInvoice, generateOldInvoice, generateRegularInvoice, generateSingaporeInvoice, generateEInvoiceFromPayload, InvoiceTransactionData, InvoiceCustomerData } from "../../services/invoiceService";
import PartnerEInvoiceModal from "./PartnerEInvoiceModal";
import * as XLSX from 'xlsx';
import '../../styles/page-base.css';

type LhdnStatus = "submitted" | "valid" | "invalid" | "cancelled" | "rejected" | null;

interface SalesDoc {
    id: string;
    date: string;
    company_name: string;
    org_id: string;
    product_name: string; 
    product_id: string;
    payment_reference: string;
    amount: number;
    credits: number;
    referral_code: string; 
    status: "paid" | "pending" | "failed" | "cancelled" | "refunded";
    product_country: string;
    currency: string;
    created_at: string;
    updated_at: string;
    sequential_invoice_number?: number;
    invoice_version?: number;
    lhdn_status: LhdnStatus;
    lhdn_submission_uid?: string | null;
    lhdn_document_uuid?: string | null;
    lhdn_long_id?: string | null;
    lhdn_consolidated_batch_id?: string | null;
}

const getLhdnBadge = (doc: Pick<SalesDoc, 'lhdn_status' | 'lhdn_consolidated_batch_id'>) => {
    if (doc.lhdn_consolidated_batch_id) {
        // Part of a consolidated batch — show "Consolidated" regardless of sub-status
        const statusSuffix = doc.lhdn_status === 'valid'     ? ' ✓'
                           : doc.lhdn_status === 'submitted' ? ' …'
                           : doc.lhdn_status === 'invalid'   ? ' ✗'
                           : '';
        return { cls: "bg-purple-100 text-purple-700", label: `Consolidated${statusSuffix}`, isConsolidated: true };
    }
    switch (doc.lhdn_status) {
        case "valid":     return { cls: "bg-green-100 text-green-700",    label: "Valid",      isConsolidated: false };
        case "submitted": return { cls: "bg-blue-100 text-blue-700",     label: "Pending",    isConsolidated: false };
        case "invalid":   return { cls: "bg-red-100 text-red-700",       label: "Invalid",    isConsolidated: false };
        case "cancelled": return { cls: "bg-gray-200 text-gray-500",     label: "Cancelled",  isConsolidated: false };
        case "rejected":  return { cls: "bg-orange-100 text-orange-700", label: "Rejected",   isConsolidated: false };
        default:          return null;
    }
};

interface TransactionFormData {
    customer: string;
    product: string;
    paymentReference: string;
}

interface Organization {
    id: string;
    name: string;
    country_iso: string;
}

interface Product {
    id: string;
    name: string;
    price: number;
    credits: number;
    country_iso: string;
    description?: string;
}

const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "cancelled":
        return "bg-gray-200 text-gray-700";
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  
export default function AdminSalesPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [documents, setDocuments] = useState<SalesDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const { selectedCompany, setSelectedCompany, userCompanies } = useCompany();
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isConsolidateModalOpen, setIsConsolidateModalOpen] = useState(false);
    const [consolidating, setConsolidating] = useState(false);
    const [consolidateResult, setConsolidateResult] = useState<{ ok: boolean; lhdn_status?: string; error?: string; consolidatedInvoiceId?: string; lineCount?: number } | null>(null);

    // Consolidated batches history
    interface ConsolidatedBatch {
        id: string;
        consolidated_invoice_id: string;
        status: string;
        submission_uid: string | null;
        document_uuid: string | null;
        long_id: string | null;
        submitted_at: string | null;
        validated_at: string | null;
        line_count: number | null;
        payload: Record<string, unknown> | null;
    }
    const [batches, setBatches] = useState<ConsolidatedBatch[]>([]);
    const [batchesLoading, setBatchesLoading] = useState(false);
    const [downloadingBatch, setDownloadingBatch] = useState<string | null>(null);
    const [isBatchHistoryOpen, setIsBatchHistoryOpen] = useState(false);
    const [isPartnerEInvoiceOpen, setIsPartnerEInvoiceOpen] = useState(false);
    const [transactionForm, setTransactionForm] = useState<TransactionFormData>({
      customer: '',
      product: '',
      paymentReference: ''
    });
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [savingTransaction, setSavingTransaction] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState<SalesDoc | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [countries, setCountries] = useState<{iso: string, name: string}[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);

    const [referralProcessing, setReferralProcessing] = useState<string | null>(null);

    // e-Invoice eligibility: org_ids that have completed their LHDN taxpayer profile
    const [orgsWithEInvoice, setOrgsWithEInvoice] = useState<Set<string>>(new Set());
    const [requestingEInvoiceId, setRequestingEInvoiceId] = useState<string | null>(null);
    // Intermediate validation polling (between "submitted" and "valid")
    const [checkingStatusId, setCheckingStatusId] = useState<string | null>(null);
    // Per-row request preview (review taxpayer details before issuing)
    const [previewDoc, setPreviewDoc] = useState<SalesDoc | null>(null);
    const [previewDetails, setPreviewDetails] = useState<any | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    // Bulk individual e-invoice request modal
    const [isBulkEInvoiceOpen, setIsBulkEInvoiceOpen] = useState(false);
    const [bulkEInvoiceRunning, setBulkEInvoiceRunning] = useState(false);
    const [bulkEInvoiceProgress, setBulkEInvoiceProgress] = useState<{ done: number; total: number; ok: number; failed: number } | null>(null);
            
    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            
            // Fetch purchases in batches to bypass 1000-row limit
            const BATCH_SIZE = 1000;
            let allPurchases: any[] = [];
            let hasMore = true;
            let offset = 0;

            while (hasMore) {
                const { data: batchPurchases, error: purchasesError } = await supabase
                    .from('org_purchases')
                    .select(`
                        id,
                        amount,
                        credits,
                        status,
                        created_at,
                        updated_at,
                        org_id,
                        product_id,
                        payment_reference,
                        sequential_invoice_number,
                        invoice_version,
                        lhdn_status,
                        lhdn_submission_uid,
                        lhdn_document_uuid,
                        lhdn_long_id,
                        lhdn_consolidated_batch_id,
                        organizations(id, name),
                        products(id, name, credit_amount, country_iso, currency),
                        referral_codes:referral_code_id(code)
                    `)
                    .eq('status', 'paid')
                    .order('created_at', { ascending: false })
                    .range(offset, offset + BATCH_SIZE - 1);

                if (purchasesError) {
                    console.error('Error fetching purchases:', purchasesError);
                    setDocuments([]);
                    return;
                }

                if (batchPurchases && batchPurchases.length > 0) {
                    allPurchases = [...allPurchases, ...batchPurchases];
                    offset += BATCH_SIZE;
                    hasMore = batchPurchases.length === BATCH_SIZE;
                } else {
                    hasMore = false;
                }
            }

            console.log(`Fetched ${allPurchases.length} purchases in total`);

            // Transform the data - use joined data directly
            const transformedData = allPurchases.map((item: any) => ({
                id: item.id,
                date: new Date(item.created_at).toLocaleString(),
                company_name: item.organizations?.name || 'Unknown Organization',
                org_id: item.org_id,
                product_name: item.products?.name || 'Unknown Product',
                amount: item.amount,
                product_id: item.product_id,
                credits: item.credits || item.products?.credit_amount || 0,
                referral_code: item.referral_codes?.code || '-',
                payment_reference: item.payment_reference || '-',
                status: item.status,
                currency: item.products?.currency || 'MYR',
                created_at: item.created_at,
                updated_at: item.updated_at,
                product_country: item.products?.country_iso || '',
                sequential_invoice_number: item.sequential_invoice_number,
                invoice_version: item.invoice_version,
                lhdn_status: item.lhdn_status ?? null,
                lhdn_submission_uid: item.lhdn_submission_uid ?? null,
                lhdn_document_uuid: item.lhdn_document_uuid ?? null,
                lhdn_long_id: item.lhdn_long_id ?? null,
                lhdn_consolidated_batch_id: item.lhdn_consolidated_batch_id ?? null,
            }));

            setDocuments(transformedData);

            // Fetch the set of orgs that have completed their e-invoice (LHDN taxpayer) profile
            // so we know which sales are eligible to issue an individual e-invoice on their behalf.
            const { data: einvoiceOrgs, error: einvoiceErr } = await supabase
                .from('org_einvoice_details')
                .select('org_id');
            if (einvoiceErr) {
                console.error('Error fetching e-invoice details orgs:', einvoiceErr);
            } else {
                setOrgsWithEInvoice(new Set((einvoiceOrgs ?? []).map((r: any) => r.org_id)));
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // A sale is eligible for an INDIVIDUAL e-invoice when it is a paid MYR purchase,
    // not yet submitted / consolidated, and the org has a completed taxpayer profile.
    const hasEInvoiceProfile = useCallback(
        (doc: SalesDoc) => orgsWithEInvoice.has(doc.org_id),
        [orgsWithEInvoice]
    );
    const isEligibleForIndividual = useCallback(
        (doc: SalesDoc) =>
            doc.currency === 'MYR' &&
            doc.status === 'paid' &&
            (doc.lhdn_status === null || doc.lhdn_status === 'invalid') &&
            !doc.lhdn_consolidated_batch_id &&
            hasEInvoiceProfile(doc),
        [hasEInvoiceProfile]
    );

    const filteredDocuments = documents.filter(
        (doc) => {
            const matchesSearch = doc.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                doc.product_name.includes(searchTerm) ||
                doc.referral_code.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Filter by country based on product
            const matchesCountry = !selectedCountry || doc.product_country === selectedCountry;
            
            return matchesSearch && matchesCountry;
    });

    const fetchCountries = useCallback(async () => {
        try {
            setLoadingCountries(true);
            const { data, error } = await supabase
                .from('countries')
                .select('iso, name')
                .order('name', { ascending: true });

            if (error) {
                console.error('Error fetching countries:', error);
                return;
            }

            setCountries(data || []);
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            setLoadingCountries(false);
        }
    }, []);

    useEffect(() => {
        fetchDocuments();
        fetchCountries();
    }, [fetchDocuments, fetchCountries]);

    const handleSelectAll = () => {
        if (selectAll) {
          setSelectedRows(new Set());
          setSelectAll(false);
        } else {
          const allIds = new Set(currentDocuments.map(doc => doc.id));
          setSelectedRows(allIds);
          setSelectAll(true);
        }
    };
      
    const handleRowSelect = (docId: string) => {
        const newSelectedRows = new Set(selectedRows);
            if (newSelectedRows.has(docId)) {
                newSelectedRows.delete(docId);
            } else {
                newSelectedRows.add(docId);
            }
            setSelectedRows(newSelectedRows);
            setSelectAll(newSelectedRows.size === currentDocuments.length);
    };

    const handleCreateTransaction = () => {
        setIsCreateModalOpen(true);
        fetchOrganizations(); // Fetch organizations when modal opens
        fetchProducts(); // Fetch products when modal opens
    };
      
    const handleTransactionFormChange = (field: keyof TransactionFormData, value: string) => {
        setTransactionForm(prev => ({
            ...prev,
            [field]: value
        }));
    };
      
    const handleTransactionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            setSavingTransaction(true);
            
            // Get the selected organization
            const selectedOrg = organizations.find(org => org.id === transactionForm.customer);
            if (!selectedOrg) {
                alert('Please select a valid customer');
                return;
            }
        
            const selectedProduct = products.find(product => product.id === transactionForm.product);
            if (!selectedProduct) {
                alert('Please select a valid product');
                return;
            }
                
            // Create transaction data for org_purchases table
            const transactionData = {
                org_id: selectedOrg.id,
                product_id: selectedProduct.id,
                payment_reference: transactionForm.paymentReference,
                amount: selectedProduct.price,
                currency: 'MYR',
                status: editingTransaction ? editingTransaction.status : 'pending',
                created_at: editingTransaction ? editingTransaction.created_at : new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
        
            let data, error;
            
            if (editingTransaction) {
                // Update existing transaction
                const { data: updateData, error: updateError } = await supabase
                    .from('org_purchases')
                    .update(transactionData)
                    .eq('id', editingTransaction.id)
                    .select();
                data = updateData;
                error = updateError;
            } else {
                // Create new transaction
                const { data: insertData, error: insertError } = await supabase
                    .from('org_purchases')
                    .insert([transactionData])
                    .select();
                data = insertData;
                error = insertError;
            }
        
            if (error) {
                console.error('Error saving transaction:', error);
                alert(`Failed to ${editingTransaction ? 'update' : 'create'} transaction. Please try again.`);
                return;
            }
        
            console.log(`Transaction ${editingTransaction ? 'updated' : 'created'} successfully:`, data);
            
            // Close modal and reset form
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setEditingTransaction(null);
            setTransactionForm({
                customer: '',
                product: '',
                paymentReference: ''
            });
            
            // Refresh the transactions list
            fetchDocuments();
            alert(`Transaction ${editingTransaction ? 'updated' : 'created'} successfully!`);
            
        } catch (error) {
            console.error('Error saving transaction:', error);
            alert(`Failed to ${editingTransaction ? 'update' : 'create'} transaction. Please try again.`);
        } finally {
            setSavingTransaction(false);
        }
    };

    const handleEditTransaction = async (transaction: SalesDoc) => {
        await fetchOrganizations();
        await fetchProducts();
        setEditingTransaction(transaction);
        
        // Pre-populate the form with existing transaction data
        setTransactionForm({
            customer: transaction.org_id,
            product: transaction.product_id, 
            paymentReference: transaction.payment_reference
        });
        
        setIsEditModalOpen(true);
    };

    const fetchOrganizations = useCallback(async () => {
        try {
          setLoadingOrganizations(true);
          const { data, error } = await supabase
            .from('organizations')
            .select('id, name, country_iso')
            .order('name', { ascending: true });
      
          if (error) {
            console.error('Error fetching organizations:', error);
            return;
          }
      
          setOrganizations(data || []);
        } catch (error) {
          console.error('Error fetching organizations:', error);
        } finally {
          setLoadingOrganizations(false);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            const { data, error } = await supabase
                .from('products')
                .select('id, name, price, credit_amount, description, country_iso')
                .order('name', { ascending: true });
    
            if (error) {
                console.error('Error fetching products:', error);
                return;
            }
    
            // Transform the data to match the Product interface
            const transformedData = data?.map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                credits: item.credit_amount, 
                country_iso: item.country_iso,
                description: item.description
            })) || [];
    
            setProducts(transformedData);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    const handleProcessReferral = async (doc: SalesDoc) => {
        if (referralProcessing) return;
        setReferralProcessing(doc.id);

        try {
            // 1. Check if buyer org has a referrer linked
            const { data: buyerOrg, error: orgErr } = await supabase
                .from('organizations')
                .select('id, referred_by')
                .eq('id', doc.org_id)
                .single();

            if (orgErr || !buyerOrg) {
                alert('Error: Could not load organization.');
                return;
            }

            if (!buyerOrg.referred_by) {
                alert(`No referrer associated.\n\n"${doc.company_name}" does not have a referrer linked. Please link the referrer in the customer database first.`);
                return;
            }

            // 2. Check if referral earning already exists for this purchase
            const { data: existingEarning } = await supabase
                .from('referral_earnings')
                .select('id, created_at')
                .eq('org_purchase_id', doc.id)
                .maybeSingle();

            if (existingEarning) {
                alert(`Credit already reimbursed.\n\nReferral earning for this purchase was already created on ${new Date(existingEarning.created_at).toLocaleString()}.`);
                return;
            }

            // 3. Check if product is referral-eligible
            const { data: product } = await supabase
                .from('products')
                .select('id, referral_eligible, referral_bonus_amount')
                .eq('id', doc.product_id)
                .single();

            if (!product || !product.referral_eligible || !product.referral_bonus_amount || product.referral_bonus_amount <= 0) {
                alert('This product is not eligible for referral bonuses.');
                return;
            }

            // 4. Get referrer name for the confirmation
            const { data: referrerOrg } = await supabase
                .from('organizations')
                .select('id, name, country_iso')
                .eq('id', buyerOrg.referred_by)
                .single();

            if (!referrerOrg) {
                alert('Error: Referrer organization not found.');
                return;
            }

            // 5. Confirm with admin
            const confirmed = window.confirm(
                `Process referral bonus?\n\n` +
                `Buyer: ${doc.company_name}\n` +
                `Referrer: ${referrerOrg.name} (${referrerOrg.country_iso})\n` +
                `Bonus: ${product.referral_bonus_amount} credits\n\n` +
                `This will add ${product.referral_bonus_amount} credits to "${referrerOrg.name}"'s wallet. Are you sure?`
            );

            if (!confirmed) return;

            // 6. Get or create wallet for the referrer
            let { data: referrerWallet } = await supabase
                .from('wallets')
                .select('id')
                .eq('org_id', referrerOrg.id)
                .maybeSingle();

            if (!referrerWallet) {
                const currencyMap: Record<string, string> = { MY: 'MYR', SG: 'SGD', HK: 'HKD' };
                const referrerCurrency = currencyMap[referrerOrg.country_iso?.trim() || ''] || 'MYR';

                const { data: newWallet, error: walletErr } = await supabase
                    .from('wallets')
                    .insert({ org_id: referrerOrg.id, currency: referrerCurrency })
                    .select('id')
                    .single();

                if (walletErr || !newWallet) {
                    alert('Failed to create referrer wallet: ' + (walletErr?.message || 'Unknown error'));
                    return;
                }
                referrerWallet = newWallet;
            }

            // 7. Create referral_earnings record
            const { error: earningErr } = await supabase
                .from('referral_earnings')
                .insert({
                    referrer_id: referrerOrg.id,
                    referred_org_id: doc.org_id,
                    org_purchase_id: doc.id,
                    product_id: doc.product_id,
                    credits_earned: product.referral_bonus_amount,
                    is_paid: true
                });

            if (earningErr) {
                alert('Failed to create referral earning: ' + earningErr.message);
                return;
            }

            // 8. Create wallet_transactions bonus for referrer
            const { error: txErr } = await supabase
                .from('wallet_transactions')
                .insert({
                    wallet_id: referrerWallet.id,
                    kind: 'bonus',
                    amount_numeric: product.referral_bonus_amount,
                    currency_balance: 0,
                    reference_type: 'referral',
                    reference_id: doc.id,
                    created_by: null
                });

            if (txErr) {
                alert('Referral earning created, but failed to create wallet bonus: ' + txErr.message);
                return;
            }

            // 9. Refresh materialized view
            await supabase.rpc('refresh_wallet_balances');

            alert(
                `Referral bonus processed!\n\n` +
                `Referrer: ${referrerOrg.name}\n` +
                `Credits added: ${product.referral_bonus_amount}\n`
            );
        } catch (error: any) {
            console.error('Error processing referral:', error);
            alert('Error: ' + (error?.message || 'Unknown error'));
        } finally {
            setReferralProcessing(null);
        }
    };

    // Generate invoice PDF blob
    const generateInvoice = async (doc: SalesDoc): Promise<Blob> => {
        let customerData: InvoiceCustomerData = {
            company_name: doc.company_name || 'Unknown Company',
            billing_address: '',
            contact_phone: undefined,
            contact_email: undefined
        };

        // Fetch organization details (address)
        const { data: orgData } = await supabase
            .from('organizations')
            .select('address')
            .eq('id', doc.org_id)
            .maybeSingle();

        if (orgData?.address) {
            customerData.billing_address = orgData.address;
        }

        // Fetch organization owner (role_id = 1 is owner)
        const { data: ownerMember } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('org_id', doc.org_id)
            .eq('role_id', 1)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();

        if (ownerMember?.user_id) {
            const { data: userDetails } = await supabase
                .rpc('get_user_details', { user_ids: [ownerMember.user_id] });

            if (userDetails && userDetails.length > 0) {
                customerData.contact_email = userDetails[0].email || undefined;
                customerData.contact_phone = userDetails[0].contact_number || undefined;
            }
        }

        const transactionData: InvoiceTransactionData = {
            id: doc.id,
            created_at: doc.created_at,
            amount: doc.amount,
            credits: doc.credits,
            products: { name: doc.product_name },
            sequential_invoice_number: doc.sequential_invoice_number
        };

        const invoiceVersion = doc.invoice_version;
        let pdfBlob: Blob;
        
        if (invoiceVersion === 3) {
            pdfBlob = await generateSSTInvoice(transactionData, customerData);
        } else if (invoiceVersion === 4) {
            pdfBlob = await generateSingaporeInvoice(transactionData, customerData);
        } else if (invoiceVersion === 2) {
            pdfBlob = await generateRegularInvoice(transactionData, customerData);
        } else {
            pdfBlob = await generateOldInvoice(transactionData, customerData);
        }

        return pdfBlob;
    };

    const handleDownloadInvoice = async (doc: SalesDoc) => {
        try {
            // If a valid LHDN e-invoice exists, generate the e-invoice PDF from the
            // stored payload (same behavior as customer-facing BillingHistory)
            if (doc.lhdn_status === 'valid' && doc.lhdn_document_uuid && doc.lhdn_long_id) {
                // Fetch payload lazily — not loaded in the list query to keep it light
                const { data: purchaseRow } = await supabase
                    .from('org_purchases')
                    .select('lhdn_payload, lhdn_submitted_at')
                    .eq('id', doc.id)
                    .single();

                if (purchaseRow?.lhdn_payload) {
                    const pdfBlob = await generateEInvoiceFromPayload(
                        purchaseRow.lhdn_payload as Record<string, unknown>,
                        doc.lhdn_document_uuid,
                        doc.lhdn_long_id,
                        purchaseRow.lhdn_submitted_at
                    );
                    const seqNum = doc.sequential_invoice_number;
                    const filename = seqNum
                        ? `EINV${seqNum.toString().padStart(6, '0')}.pdf`
                        : `EINV-${doc.id.slice(0, 8)}.pdf`;
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url; a.download = filename; a.click();
                    URL.revokeObjectURL(url);
                    return;
                }
            }

            const pdfBlob = await generateInvoice(doc);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            
            const invoiceVersion = doc.invoice_version;
            let filename: string;
            if (invoiceVersion === 3) {
                const sequentialNumber = doc.sequential_invoice_number;
                filename = `INV${sequentialNumber?.toString().padStart(6, '0')}.pdf`;
            } else {
                let invoiceId = doc.id;
                if (invoiceId.includes('-')) {
                    invoiceId = invoiceId.split('-')[0];
                } else {
                    invoiceId = invoiceId.substring(0, 8);
                }
                filename = `invoice_${invoiceId}.pdf`;
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pdfUrl);
        } catch (error) {
            console.error('Error generating invoice:', error);
            alert('Error generating invoice. Please try again.');
        }
    };

    const isEligibleForConsolidation = (d: SalesDoc) =>
        d.currency === 'MYR' && d.status === 'paid' && d.lhdn_status === null && !d.lhdn_consolidated_batch_id;

    // Open the review dialog: fetch the customer's taxpayer profile before issuing
    const openEInvoicePreview = async (doc: SalesDoc) => {
        setPreviewDoc(doc);
        setPreviewDetails(null);
        setPreviewLoading(true);
        try {
            const { data, error } = await supabase
                .from('org_einvoice_details')
                .select('tin, id_type, brn, legal_name, email, phone, sst_registration, address_line_1, address_line_2, address_line_3, city, postal_code, state_code')
                .eq('org_id', doc.org_id)
                .maybeSingle();
            if (error) {
                console.error('Error loading e-invoice details:', error);
            }
            setPreviewDetails(data ?? null);
        } finally {
            setPreviewLoading(false);
        }
    };

    // Request an INDIVIDUAL e-invoice on the customer's behalf (uses their taxpayer profile)
    const handleRequestEInvoice = async (doc: SalesDoc) => {
        try {
            setRequestingEInvoiceId(doc.id);
            const { data, error } = await supabase.functions.invoke('submit-einvoice-to-lhdn', {
                body: { org_purchase_id: doc.id },
            });
            if (error || (data && data.ok === false)) {
                const detail = (data && (data.error || JSON.stringify(data.lhdn_response))) || error?.message || 'Unknown error';
                alert(`Failed to issue e-invoice for ${doc.company_name}: ${detail}`);
                return;
            }
            // Close preview on success
            setPreviewDoc(null);
            setPreviewDetails(null);
            await fetchDocuments();
        } catch (err) {
            console.error('Error requesting e-invoice:', err);
            alert('Failed to issue e-invoice. Please try again.');
        } finally {
            setRequestingEInvoiceId(null);
        }
    };

    // Intermediate step: poll LHDN for the validation result of a submitted e-invoice.
    // A submission may not validate instantly — it sits at "submitted" until LHDN finishes,
    // and the long_id (needed for the validation link) is only available once valid.
    const handleCheckLhdnStatus = async (doc: SalesDoc) => {
        if (!doc.lhdn_submission_uid && !doc.lhdn_document_uuid) return;
        try {
            setCheckingStatusId(doc.id);
            const { data, error } = await supabase.functions.invoke('check-lhdn-status', {
                body: { org_purchase_id: doc.id },
            });
            if (error) {
                alert('Status check failed: ' + error.message);
                return;
            }
            setDocuments(prev => prev.map(d =>
                d.id === doc.id
                    ? {
                        ...d,
                        lhdn_status: data?.lhdn_status ?? d.lhdn_status,
                        lhdn_long_id: data?.lhdn_long_id ?? d.lhdn_long_id,
                        lhdn_document_uuid: data?.lhdn_document_uuid ?? d.lhdn_document_uuid,
                    }
                    : d
            ));
        } catch (e) {
            alert('Status check error: ' + String(e));
        } finally {
            setCheckingStatusId(null);
        }
    };

    // Bulk: issue individual e-invoices for all selected eligible rows (one call each)
    const handleBulkRequestEInvoice = async () => {
        const selectedIds = Array.from(selectedRows);
        const eligible = documents.filter(d => selectedIds.includes(d.id) && isEligibleForIndividual(d));
        if (eligible.length === 0) {
            alert('No eligible rows selected. Eligible = paid MYR sale, not yet submitted, and the customer has completed their e-invoice profile.');
            return;
        }
        setBulkEInvoiceProgress({ done: 0, total: eligible.length, ok: 0, failed: 0 });
        setIsBulkEInvoiceOpen(true);
    };

    const runBulkRequestEInvoice = async () => {
        const selectedIds = Array.from(selectedRows);
        const eligible = documents.filter(d => selectedIds.includes(d.id) && isEligibleForIndividual(d));
        if (eligible.length === 0) return;
        setBulkEInvoiceRunning(true);
        let ok = 0;
        let failed = 0;
        try {
            for (let i = 0; i < eligible.length; i++) {
                const doc = eligible[i];
                try {
                    const { data, error } = await supabase.functions.invoke('submit-einvoice-to-lhdn', {
                        body: { org_purchase_id: doc.id },
                    });
                    if (error || (data && data.ok === false)) {
                        failed++;
                    } else {
                        ok++;
                    }
                } catch {
                    failed++;
                }
                setBulkEInvoiceProgress({ done: i + 1, total: eligible.length, ok, failed });
            }
            await fetchDocuments();
            setSelectedRows(new Set());
            setSelectAll(false);
        } finally {
            setBulkEInvoiceRunning(false);
        }
    };

    const handleConsolidatedEInvoice = async () => {
        const selectedIds = Array.from(selectedRows);
        const eligible = documents.filter(d => selectedIds.includes(d.id) && isEligibleForConsolidation(d));
        if (eligible.length === 0) {
            alert('No eligible invoices selected. Select MYR paid invoices that have not yet been submitted to LHDN.');
            return;
        }
        setConsolidateResult(null);
        setIsConsolidateModalOpen(true);
    };

    const handleSubmitConsolidate = async () => {
        const selectedIds = Array.from(selectedRows);
        const eligible = documents.filter(d => selectedIds.includes(d.id) && isEligibleForConsolidation(d));
        if (eligible.length === 0) return;
        setConsolidating(true);
        try {
            const { data: result, error: invokeError } = await supabase.functions.invoke('submit-consolidated-einvoice', {
                body: { org_purchase_ids: eligible.map(d => d.id) },
            });
            if (invokeError) throw invokeError;
            setConsolidateResult({
                ok: result.ok === true,
                lhdn_status: result.lhdn_status,
                error: result.error ?? (result.lhdn_response ? JSON.stringify(result.error ?? result.lhdn_response) : undefined),
                consolidatedInvoiceId: result.consolidatedInvoiceId,
                lineCount: result.lineCount,
            });
            if (result.ok) {
                setSelectedRows(new Set());
                setSelectAll(false);
                fetchDocuments();
            }
        } catch (err) {
            setConsolidateResult({ ok: false, error: err instanceof Error ? err.message : 'Network error' });
        } finally {
            setConsolidating(false);
        }
    };

    const handleDownloadSales = () => {
        if (documents.length === 0) return;

        const exportData = documents.map(doc => ({
            'Invoice No': doc.sequential_invoice_number ? `INV${doc.sequential_invoice_number.toString().padStart(6, '0')}` : '-',
            'Date': new Date(doc.created_at).toLocaleDateString('en-GB'),
            'Company': doc.company_name,
            'Product': doc.product_name,
            'Currency': doc.currency,
            'Amount': doc.amount,
            'Credits': doc.credits,
            'Referral Code': doc.referral_code,
            'Payment Ref': doc.payment_reference,
            'Status': doc.status
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 12 }, { wch: 12 }, { wch: 35 }, { wch: 25 }, { wch: 10 },
            { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 10 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sales');

        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `sales_records_${dateStr}.xlsx`);
    };


  const totalPages = Math.ceil(filteredDocuments.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
        const { data, error } = await supabase
            .from('lhdn_consolidated_batches')
            .select('id, consolidated_invoice_id, status, submission_uid, document_uuid, long_id, submitted_at, validated_at, line_count')
            .order('submitted_at', { ascending: false });
        if (!error && data) setBatches(data as ConsolidatedBatch[]);
    } catch (e) {
        console.error('Failed to fetch consolidated batches', e);
    } finally {
        setBatchesLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleDownloadBatchInvoice = async (batch: ConsolidatedBatch) => {
    if (batch.status !== 'valid' || !batch.document_uuid || !batch.long_id) {
        alert('Consolidated e-invoice is not yet validated. No PDF available.');
        return;
    }
    setDownloadingBatch(batch.id);
    try {
        // Fetch payload lazily (not included in list query for performance)
        const { data: batchRow } = await supabase
            .from('lhdn_consolidated_batches')
            .select('payload, submitted_at')
            .eq('id', batch.id)
            .single();

        if (!batchRow?.payload) {
            alert('Payload not found for this batch.');
            return;
        }
        const pdfBlob = await generateEInvoiceFromPayload(
            batchRow.payload as Record<string, unknown>,
            batch.document_uuid,
            batch.long_id,
            batchRow.submitted_at
        );
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${batch.consolidated_invoice_id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error('Error downloading batch PDF', e);
        alert('Failed to generate PDF.');
    } finally {
        setDownloadingBatch(null);
    }
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
            
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
            <div className="text-sm text-gray-500 mb-1">Admin Only</div>
            <h1 className="page-title">Sales</h1>
            </div>
            <div className="page-header-right">
            <button
                onClick={handleCreateTransaction}
                className="f-btn f-btn-primary"
                >
                <span>New Sales</span>
            </button>
            <button
                onClick={() => setIsPartnerEInvoiceOpen(true)}
                className="f-btn f-btn-soft"
                title="Issue an e-invoice to a partner, such as shared office rental"
            >
                <span>Partner e-Invoice</span>
            </button>
            <button
                onClick={() => { fetchBatches(); setIsBatchHistoryOpen(true); }}
                className="f-btn f-btn-soft"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span>Consolidated History</span>
            </button>
                {selectedRows.size > 0 && (() => {
                    const eligibleIndividual = documents.filter(d => selectedRows.has(d.id) && isEligibleForIndividual(d)).length;
                    return (
                        <>
                            {eligibleIndividual > 0 && (
                                <button
                                    onClick={handleBulkRequestEInvoice}
                                    className="f-btn f-btn-primary"
                                    title="Issue individual e-invoices for selected customers who have completed their taxpayer profile"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                    <span>Issue e-Invoice ({eligibleIndividual})</span>
                                </button>
                            )}
                            <button
                                onClick={handleConsolidatedEInvoice}
                                className="f-btn f-btn-soft"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Consolidated e-Invoice ({selectedRows.size})</span>
                            </button>
                        </>
                    );
                })()}
                <button onClick={handleDownloadSales}
                  className="f-btn f-btn-success">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Export Excel</span>
                </button>
            </div>
          </div>

        <div className="page-card">
          <div className="flex items-center space-x-3 mb-4">
            <select 
                value={selectedCountry || ''} 
                onChange={(e) => setSelectedCountry(e.target.value || null)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
                <option value="">All Countries</option>
                {countries.map(country => (
                    <option key={country.iso} value={country.iso}>
                        {country.name}
                    </option>
                ))}
            </select>
            <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                </div>
                <input
                type="text"
                placeholder="Search by Company Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
            {/* Select eligible invoices from previous calendar month (Malaysia UTC+8) for consolidation */}
            <button
                onClick={() => {
                    const MY_OFFSET = 8 * 60; // UTC+8 in minutes
                    const nowUtc = new Date();
                    const nowMY = new Date(nowUtc.getTime() + MY_OFFSET * 60_000);
                    // First day of current month in MY time → step back to get prev month
                    const prevMonthMY = new Date(Date.UTC(nowMY.getUTCFullYear(), nowMY.getUTCMonth() - 1, 1));
                    const prevMonthStart = new Date(prevMonthMY.getTime() - MY_OFFSET * 60_000); // back to UTC wall-clock
                    const prevMonthEnd = new Date(Date.UTC(nowMY.getUTCFullYear(), nowMY.getUTCMonth(), 1) - MY_OFFSET * 60_000);
                    const ids = new Set(
                        documents
                            .filter(d => {
                                const t = new Date(d.created_at).getTime();
                                const inPrevMonth = t >= prevMonthStart.getTime() && t < prevMonthEnd.getTime();
                                return inPrevMonth && isEligibleForConsolidation(d);
                            })
                            .map(d => d.id)
                    );
                    setSelectedRows(ids);
                    setSelectAll(false);
                }}
                className="ml-auto border border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg px-3 py-2 text-sm whitespace-nowrap flex items-center gap-1.5 transition"
                title="Select all eligible (unsubmitted MYR) invoices from last month for consolidated e-invoice"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Select Last Month Invoice for Consolidation
            </button>
          </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
            <table className="page-table">
                <thead className="page-table-thead">
                <tr className="page-table-tr">
                <th className="page-table-th">
                    <input 
                        type="checkbox" 
                        className="rounded border-gray-300" 
                        checked={selectAll}
                        onChange={handleSelectAll}
                    />
                </th>
                    <th className="page-table-th">Date</th>  
                    <th className="page-table-th">Company</th>  
                    <th className="page-table-th">Product</th>
                    <th className="page-table-th">Amount</th>
                    <th className="page-table-th">Credits</th>
                    <th className="page-table-th">Referral Code</th>
                    <th className="page-table-th">Status</th>
                    <th className="page-table-th">e-Invoice</th>
                    <th className="page-table-th">Actions</th>
                </tr>
                </thead>

                <tbody className="page-table-tbody">
                {loading ? (
                    <tr className="page-table-tr">
                    <td className="page-table-td" colSpan={10}>
                        Loading...
                    </td>
                    </tr>
                ) : currentDocuments.length === 0 ? (
                    <tr className="page-table-tr">
                    <td className="page-table-td" colSpan={10}>
                        No transactions found.
                    </td>
                    </tr>
                ) : (
                    currentDocuments.map((doc, idx) => (
                    <tr className="page-table-tr"
                        key={doc.id}
                    >
                        <td className="page-table-td">
                            <input 
                                type="checkbox" 
                                className="rounded border-gray-300" 
                                checked={selectedRows.has(doc.id)}
                                onChange={() => handleRowSelect(doc.id)}
                            />
                        </td>
                        <td className="page-table-td">
                            {(() => {
                                const dateObj = new Date(doc.created_at);
                                const dateStr = dateObj.toLocaleDateString('en-GB');
                                const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                });
                                return (
                                    <div className="flex flex-col">
                                        <span>{dateStr}</span>
                                        <span className="text-gray-500 text-xs">{timeStr}</span>
                                    </div>
                                );
                            })()}
                        </td>
                         <td className="page-table-td">{doc.company_name}</td>
                         <td className="page-table-td">{doc.product_name}</td>
                        <td className="page-table-td text-center">
                            <div className="flex flex-col">
                                <span>{doc.currency}</span>
                                <span>{doc.amount}</span>
                            </div>
                        </td>
                        <td className="page-table-td text-center">{doc.credits}</td>
                        <td className="page-table-td">{doc.referral_code}</td>
                        <td className="page-table-td">
                        <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            doc.status
                            )}`}
                        >
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                        </td>
                        <td className="page-table-td text-center">
                            {(() => {
                                const badge = getLhdnBadge(doc);
                                const canRequest = isEligibleForIndividual(doc);
                                const isMyPaid = doc.currency === 'MYR' && doc.status === 'paid';
                                const requesting = requestingEInvoiceId === doc.id;
                                return (
                                    <div className="flex flex-col items-center gap-1">
                                        {badge ? (
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                                        ) : (
                                            <span className="text-gray-300 text-xs">—</span>
                                        )}
                                        {!badge?.isConsolidated && doc.lhdn_status === 'valid' && doc.lhdn_document_uuid && doc.lhdn_long_id && (
                                            <a
                                                href={`https://myinvois.hasil.gov.my/${doc.lhdn_document_uuid}/share/${doc.lhdn_long_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:underline"
                                                title="LHDN Validation Link"
                                            >
                                                View
                                            </a>
                                        )}
                                        {/* Intermediate step: pending validation, or valid but long_id not yet retrieved */}
                                        {!badge?.isConsolidated && (doc.lhdn_status === 'submitted' || (doc.lhdn_status === 'valid' && !doc.lhdn_long_id)) && (
                                            <button
                                                onClick={() => handleCheckLhdnStatus(doc)}
                                                disabled={checkingStatusId === doc.id}
                                                className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                                                title="Check LHDN validation status"
                                            >
                                                {checkingStatusId === doc.id ? 'Checking…' : (doc.lhdn_status === 'valid' ? 'Get Link' : 'Refresh')}
                                            </button>
                                        )}
                                        {/* Admin can issue on the customer's behalf when eligible */}
                                        {canRequest && (
                                            <button
                                                onClick={() => openEInvoicePreview(doc)}
                                                disabled={requesting}
                                                className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                                                title="Review taxpayer details and issue an individual e-invoice"
                                            >
                                                {requesting ? 'Issuing…' : (doc.lhdn_status === 'invalid' ? 'Retry' : 'Request')}
                                            </button>
                                        )}
                                        {/* Explain why not requestable */}
                                        {!canRequest && !badge && isMyPaid && !doc.lhdn_consolidated_batch_id && !hasEInvoiceProfile(doc) && (
                                            <span className="text-[10px] text-gray-400" title="Customer has not completed their e-invoice taxpayer profile">
                                                No profile
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}
                        </td>
                        <td className="page-table-td">
                          <div className="flex justify-center space-x-2">
                            <button className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition" onClick={() => handleEditTransaction(doc)}>
                              Edit
                            </button>
                            <button className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition" onClick={() => handleDownloadInvoice(doc)}>
                            Download
                            </button>
                            <button
                              className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition disabled:opacity-50"
                              onClick={() => handleProcessReferral(doc)}
                              disabled={referralProcessing === doc.id}
                            >
                              {referralProcessing === doc.id ? '...' : 'Referral'}
                            </button>
                        </div>
                        </td>
                    </tr>
                    ))
                )}
                </tbody>
            </table>
            </div>



          {/* Pagination */}
          {!loading && filteredDocuments.length > 0 && (
            <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                    <div className="flex items-center space-x-2">
                        <span>Rows per page:</span>
                        <select 
                            value={rowsPerPage} 
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                <span>
                    {startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)} of {filteredDocuments.length}
                </span>
              <div className="flex space-x-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                >
                  &lt;
                </button>
                        <button
                            className="f-btn f-btn-primary f-btn-xs"
                        >
                            {currentPage}
                        </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    <PartnerEInvoiceModal open={isPartnerEInvoiceOpen} onClose={() => setIsPartnerEInvoiceOpen(false)} />

    {/* Consolidated Batch History Modal */}
    {isBatchHistoryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-800">Consolidated e-Invoice History</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Each row is one LHDN consolidated document covering multiple invoices.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => { fetchBatches(); }} className="text-gray-400 hover:text-gray-600" title="Refresh">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        </button>
                        <button onClick={() => setIsBatchHistoryOpen(false)} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>
                <div className="overflow-y-auto flex-1 p-6">
                    {batchesLoading ? (
                        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
                    ) : batches.length === 0 ? (
                        <p className="text-sm text-gray-400 py-8 text-center">No consolidated batches submitted yet.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
                                    <th className="text-left pb-3 pr-4 font-medium">Invoice ID</th>
                                    <th className="text-center pb-3 px-3 font-medium">Lines</th>
                                    <th className="text-center pb-3 px-3 font-medium">Status</th>
                                    <th className="text-left pb-3 px-3 font-medium">Submitted</th>
                                    <th className="text-left pb-3 px-3 font-medium">Validated</th>
                                    <th className="text-center pb-3 pl-3 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {batches.map(batch => {
                                    const statusMap: Record<string, { cls: string; label: string }> = {
                                        valid:     { cls: 'bg-green-100 text-green-700',    label: 'Valid' },
                                        submitted: { cls: 'bg-blue-100 text-blue-700',     label: 'Pending' },
                                        invalid:   { cls: 'bg-red-100 text-red-700',       label: 'Invalid' },
                                        cancelled: { cls: 'bg-gray-200 text-gray-500',     label: 'Cancelled' },
                                        rejected:  { cls: 'bg-orange-100 text-orange-700', label: 'Rejected' },
                                    };
                                    const badge = statusMap[batch.status] ?? { cls: 'bg-gray-100 text-gray-500', label: batch.status };
                                    const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—';
                                    return (
                                        <tr key={batch.id} className="hover:bg-gray-50">
                                            <td className="py-3 pr-4">
                                                <span className="font-mono text-xs font-medium text-gray-800 block">{batch.consolidated_invoice_id}</span>
                                                {batch.submission_uid && (
                                                    <span className="text-xs text-gray-400 truncate block max-w-[200px]" title={batch.submission_uid}>{batch.submission_uid}</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-center text-gray-700">{batch.line_count ?? '—'}</td>
                                            <td className="py-3 px-3 text-center">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                                            </td>
                                            <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(batch.submitted_at)}</td>
                                            <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(batch.validated_at)}</td>
                                            <td className="py-3 pl-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    {batch.status === 'valid' && batch.document_uuid && batch.long_id && (
                                                        <a
                                                            href={`https://myinvois.hasil.gov.my/${batch.document_uuid}/share/${batch.long_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="px-2 py-1 text-xs rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition"
                                                        >
                                                            Validate
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => handleDownloadBatchInvoice(batch)}
                                                        disabled={batch.status !== 'valid' || downloadingBatch === batch.id}
                                                        className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 flex items-center gap-1"
                                                        title={batch.status !== 'valid' ? 'Available once validated' : 'Download PDF'}
                                                    >
                                                        {downloadingBatch === batch.id
                                                            ? <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                                            : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                        }
                                                        PDF
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="px-6 py-3 border-t flex justify-end">
                    <button onClick={() => setIsBatchHistoryOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Close</button>
                </div>
            </div>
        </div>
    )}

    {/* Individual e-Invoice Preview / Confirm Modal */}
    {previewDoc && (() => {
        const d = previewDetails;
        const idTypeLabel = d?.id_type || 'ID';
        const addressLines = [d?.address_line_1, d?.address_line_2, d?.address_line_3].filter(Boolean);
        const issuing = requestingEInvoiceId === previewDoc.id;
        const Row = ({ label, value }: { label: string; value?: string }) => (
            <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-xs text-gray-500 shrink-0">{label}</span>
                <span className="text-xs text-gray-800 text-right break-words">{value && value.trim() ? value : <span className="text-gray-300">—</span>}</span>
            </div>
        );
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[85vh]">
                    <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">Review e-Invoice Details</h2>
                            <p className="text-xs text-gray-500 mt-0.5">{previewDoc.company_name} · {previewDoc.currency} {previewDoc.amount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</p>
                        </div>
                        {!issuing && (
                            <button onClick={() => { setPreviewDoc(null); setPreviewDetails(null); }} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        )}
                    </div>
                    <div className="overflow-y-auto flex-1 px-5 py-4">
                        {previewLoading ? (
                            <p className="text-sm text-gray-400 py-6 text-center">Loading taxpayer profile…</p>
                        ) : !d ? (
                            <p className="text-sm text-red-600 py-6 text-center">No taxpayer profile found for this customer.</p>
                        ) : (
                            <div className="space-y-0.5">
                                <Row label="Legal / Registered Name" value={d.legal_name} />
                                <Row label="TIN" value={d.tin} />
                                <Row label={idTypeLabel} value={d.brn} />
                                <Row label="SST Registration" value={d.sst_registration} />
                                <Row label="Email" value={d.email} />
                                <Row label="Phone" value={d.phone} />
                                <Row label="Address" value={addressLines.join(', ')} />
                                <Row label="City" value={d.city} />
                                <Row label="Postal Code" value={d.postal_code} />
                                <Row label="State Code" value={d.state_code} />
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 px-5 py-4 border-t shrink-0">
                        <button onClick={() => { setPreviewDoc(null); setPreviewDetails(null); }} disabled={issuing} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                        <button
                            onClick={() => handleRequestEInvoice(previewDoc)}
                            disabled={issuing || previewLoading || !d}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {issuing && <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
                            {issuing ? 'Issuing…' : 'Confirm & Issue'}
                        </button>
                    </div>
                </div>
            </div>
        );
    })()}

    {/* Bulk Individual e-Invoice Modal */}
    {isBulkEInvoiceOpen && (() => {
        const eligible = documents.filter(d => selectedRows.has(d.id) && isEligibleForIndividual(d));
        const progress = bulkEInvoiceProgress;
        const finished = !!progress && !bulkEInvoiceRunning && progress.done >= progress.total && progress.total > 0;
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
                    <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">Issue Individual e-Invoices</h2>
                            <p className="text-xs text-gray-500 mt-0.5">{eligible.length} eligible customer{eligible.length !== 1 ? 's' : ''} · each gets their own LHDN e-invoice</p>
                        </div>
                        {!bulkEInvoiceRunning && (
                            <button onClick={() => { setIsBulkEInvoiceOpen(false); setBulkEInvoiceProgress(null); }} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        )}
                    </div>
                    <div className="overflow-y-auto flex-1 p-5 space-y-4">
                        {progress && (progress.done > 0 || bulkEInvoiceRunning) ? (
                            <div className="space-y-3">
                                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                    <div className="bg-blue-600 h-2.5 transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
                                </div>
                                <p className="text-sm text-gray-600">Processed {progress.done} / {progress.total}</p>
                                <div className="flex gap-4 text-sm">
                                    <span className="text-green-600">✓ {progress.ok} issued</span>
                                    <span className="text-red-600">✗ {progress.failed} failed</span>
                                </div>
                                {finished && (
                                    <p className="text-sm text-gray-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                                        Done. {progress.ok} e-invoice{progress.ok !== 1 ? 's' : ''} issued{progress.failed > 0 ? `, ${progress.failed} failed (retry from the row action).` : '.'}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-gray-600">
                                    This issues a separate LHDN e-invoice for each selected customer using their own taxpayer profile.
                                    Invoices already submitted, consolidated, or without a profile are skipped.
                                </p>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Date</th>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Inv No</th>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Company</th>
                                                <th className="text-right px-3 py-2 text-gray-500 font-medium">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {eligible.map(d => (
                                                <tr key={d.id}>
                                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                                                    <td className="px-3 py-2 text-gray-700">{d.sequential_invoice_number ? `INV${String(d.sequential_invoice_number).padStart(6,'0')}` : d.id.slice(0,8)}</td>
                                                    <td className="px-3 py-2 text-gray-700">{d.company_name}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{d.currency} {d.amount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {eligible.length === 0 && <p className="text-sm text-gray-500">No eligible customers to issue.</p>}
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 px-5 py-4 border-t shrink-0">
                        {finished ? (
                            <button onClick={() => { setIsBulkEInvoiceOpen(false); setBulkEInvoiceProgress(null); }} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Close</button>
                        ) : (
                            <>
                                <button onClick={() => setIsBulkEInvoiceOpen(false)} disabled={bulkEInvoiceRunning} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                                <button
                                    onClick={runBulkRequestEInvoice}
                                    disabled={bulkEInvoiceRunning || eligible.length === 0}
                                    className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {bulkEInvoiceRunning && <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
                                    {bulkEInvoiceRunning ? 'Issuing…' : `Issue ${eligible.length} e-Invoice${eligible.length !== 1 ? 's' : ''}`}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    })()}

    {/* Consolidated e-Invoice Modal */}
    {isConsolidateModalOpen && (() => {
        const selectedIds = Array.from(selectedRows);
        const eligible = documents.filter(d => selectedIds.includes(d.id) && isEligibleForConsolidation(d));
        const skipped  = documents.filter(d => selectedIds.includes(d.id) && !isEligibleForConsolidation(d));
        const totalAmt = eligible.reduce((s, d) => s + d.amount, 0);
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
                    {/* Sticky header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">Submit Consolidated e-Invoice</h2>
                            {!consolidateResult && <p className="text-xs text-gray-500 mt-0.5">{eligible.length} invoice{eligible.length !== 1 ? 's' : ''} · MYR {totalAmt.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</p>}
                        </div>
                        <button onClick={() => { setIsConsolidateModalOpen(false); setConsolidateResult(null); }} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                    {/* Scrollable body */}
                    <div className="overflow-y-auto flex-1 p-5 space-y-4">
                        {consolidateResult ? (
                            <div className={`rounded-lg p-4 ${consolidateResult.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                {consolidateResult.ok ? (
                                    <>
                                        <p className="text-green-700 font-medium text-sm">Submission successful</p>
                                        <p className="text-green-600 text-xs mt-1">Invoice ID: {consolidateResult.consolidatedInvoiceId} · Status: {consolidateResult.lhdn_status} · {consolidateResult.lineCount} lines</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-red-700 font-medium text-sm">Submission failed</p>
                                        {consolidateResult.error && <p className="text-red-600 text-xs mt-1 break-all">{consolidateResult.error}</p>}
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Date</th>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Inv No</th>
                                                <th className="text-left px-3 py-2 text-gray-500 font-medium">Company</th>
                                                <th className="text-right px-3 py-2 text-gray-500 font-medium">Amount (MYR)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {eligible.map(d => (
                                                <tr key={d.id}>
                                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                                                    <td className="px-3 py-2 text-gray-700">{d.sequential_invoice_number ? `INV${String(d.sequential_invoice_number).padStart(6,'0')}` : d.id.slice(0,8)}</td>
                                                    <td className="px-3 py-2 text-gray-700">{d.company_name}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{d.amount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-gray-50">
                                            <tr>
                                                <td colSpan={3} className="px-3 py-2 font-medium text-gray-700">Total</td>
                                                <td className="px-3 py-2 text-right font-medium text-gray-700">{totalAmt.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                {skipped.length > 0 && (
                                    <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">{skipped.length} selected invoice{skipped.length !== 1 ? 's' : ''} skipped (already submitted, non-MYR, or not paid).</p>
                                )}
                                {eligible.length === 0 && (
                                    <p className="text-sm text-gray-500">No eligible invoices to submit.</p>
                                )}
                            </>
                        )}
                    </div>
                    {/* Sticky footer */}
                    {!consolidateResult && (
                        <div className="flex justify-end gap-3 px-5 py-4 border-t shrink-0">
                            <button onClick={() => setIsConsolidateModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button
                                onClick={handleSubmitConsolidate}
                                disabled={consolidating || eligible.length === 0}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {consolidating && <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
                                {consolidating ? 'Submitting...' : `Submit ${eligible.length} Invoice${eligible.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    )}
                    {consolidateResult && (
                        <div className="flex justify-end px-5 py-4 border-t">
                            <button onClick={() => { setIsConsolidateModalOpen(false); setConsolidateResult(null); }} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Close</button>
                        </div>
                    )}
                </div>
            </div>
        );
    })()}

    {/* Create Transaction Modal */}
    {isCreateModalOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto relative z-[9999]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
            <div>
            <h2 className="text-2xl font-bold text-gray-900">Create</h2>
            <p className="text-sm text-gray-600 mt-1">Create a new transaction</p>
            </div>
            <button
            onClick={() => setIsCreateModalOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            </button>
        </div>

        {/* Form */}
        <form onSubmit={handleTransactionSubmit} className="p-6">
            <div className="space-y-6">
            {/* New Transaction Section */}
            <div>
                {/* <h3 className="text-lg font-semibold text-blue-600 mb-4">New Transaction</h3> */}
                
                <div className="space-y-4">
                {/* Customer Field - Now using organizations from database */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                    </label>
                    <select
                    value={transactionForm.customer}
                    onChange={(e) => handleTransactionFormChange('customer', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={loadingOrganizations}
                    >
                        <option value="">
                            {loadingOrganizations ? 'Loading organizations...' : 'Select Customer'}
                        </option>
                        {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                            {org.name} ({org.country_iso})
                            </option>
                        ))}
                    </select>
                </div>

                {/* Product Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product
                    </label>
                    <select
                    value={transactionForm.product}
                    onChange={(e) => handleTransactionFormChange('product', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={loadingProducts}
                    >
                        <option value="">
                        {loadingProducts ? 'Loading products...' : 'Select Product'}
                        </option>
                        {products.map((product) => (
                        <option key={product.id} value={product.id}>
                            {product.name} - RM{product.price.toFixed(2)} ({product.credits} credits)
                        </option>
                        ))}
                    </select>
                </div>

                {/* Payment Reference Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Reference
                    </label>
                    <input
                    type="text"
                    value={transactionForm.paymentReference}
                    onChange={(e) => handleTransactionFormChange('paymentReference', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Bank transfer reference number"
                    required
                    />
                </div>
                </div>
            </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t">
            <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="f-btn f-btn-soft"
                disabled={savingTransaction}
            >
                Cancel
            </button>
            <button
                type="submit"
                disabled={savingTransaction}
                className="f-btn f-btn-primary"
            >
                {savingTransaction ? 'Creating...' : 'Create Transaction'}
            </button>
            </div>
        </form>
        </div>
    </div>
    )}    

    {/* Edit Transaction Modal */}
    {isEditModalOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto relative z-[9999]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
            <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Transaction</h2>
            <p className="text-sm text-gray-600 mt-1">Update transaction information</p>
            </div>
            <button
            onClick={() => {
                setIsEditModalOpen(false);
                setEditingTransaction(null);
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            </button>
        </div>

        {/* Form */}
        <form onSubmit={handleTransactionSubmit} className="p-6">
            <div className="space-y-6">
            <div>
                <div className="space-y-4">
                {/* Customer Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                    </label>
                    <select
                    value={transactionForm.customer}
                    onChange={(e) => handleTransactionFormChange('customer', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={loadingOrganizations}
                    >
                        <option value="">
                            {loadingOrganizations ? 'Loading organizations...' : 'Select Customer'}
                        </option>
                        {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                            {org.name} ({org.country_iso})
                            </option>
                        ))}
                    </select>
                </div>

                {/* Product Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product
                    </label>
                    <select
                    value={transactionForm.product}
                    onChange={(e) => handleTransactionFormChange('product', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={loadingProducts}
                    >
                        <option value="">
                        {loadingProducts ? 'Loading products...' : 'Select Product'}
                        </option>
                        {products.map((product) => (
                        <option key={product.id} value={product.id}>
                            {product.name} - RM{product.price.toFixed(2)} ({product.credits} credits)
                        </option>
                        ))}
                    </select>
                </div>

                {/* Payment Reference Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Reference
                    </label>
                    <input
                    type="text"
                    value={transactionForm.paymentReference}
                    onChange={(e) => handleTransactionFormChange('paymentReference', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Bank transfer reference number"
                    required
                    />
                </div>
                </div>
            </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t">
            <button
                type="button"
                onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingTransaction(null);
                }}
                className="f-btn f-btn-soft"
                disabled={savingTransaction}
            >
                Cancel
            </button>
            <button
                type="submit"
                disabled={savingTransaction}
                className="f-btn f-btn-primary"
            >
                {savingTransaction ? 'Updating...' : 'Update Transaction'}
            </button>
            </div>
        </form>
        </div>
    </div>
    )}
    </MainLayout>
  );
}
